import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Bookmark, BookmarkCheck, CheckCircle2, Printer, RotateCcw, Search } from "lucide-react";
import AccountCreation from "./components/AccountCreation";
import { PageShell, usePageTitle } from "./components/site/Layout";
import { buttons, container } from "./components/site/ui";
import {
  buildSummaryTiles,
  FaultList,
  Finding,
  MileageChart,
  mileageFindings,
  MileageTable,
  ModelRatings,
  MotTestList,
  Plate,
  RiskScore,
  SectionTitle,
  StatTile,
} from "./components/report/ReportParts";
import { API_URL } from "./config";
import { saveSearch, useCurrentUser } from "./lib/auth";
import {
  analyseMileage,
  formatDate,
  formatNumber,
  formatReg,
  isValidReg,
  normaliseReg,
  normaliseReport,
  sortFaults,
  stringList,
  summariseMot,
  vehicleSummaryLine,
} from "./lib/report";
import { SAMPLE_MILEAGE, SAMPLE_REGISTRATION, SAMPLE_REPORT } from "./lib/sampleReport";

function describeError(error, registration) {
  const status = error.response?.status;
  if (status === 404 || status === 400) {
    return {
      title: "We couldn't find that registration",
      message: `The DVSA has no MOT record for ${formatReg(registration)}. Check the registration is right, or search by make and model instead.`,
      notFound: true,
    };
  }
  if (error.code === "ECONNABORTED") {
    return {
      title: "This is taking longer than usual",
      message: "The server didn't respond in time. It may have been starting up, so please try again.",
    };
  }
  if (!error.response) {
    return { title: "We couldn't reach Car Sense", message: "Check your internet connection and try again." };
  }
  return { title: "Something went wrong", message: "We couldn't build this report. Please try again in a moment." };
}

export default function Results({ sample = false }) {
  const [params] = useSearchParams();
  const registration = sample ? SAMPLE_REGISTRATION : normaliseReg(params.get("registration") || "");
  const mileageParam = sample ? String(SAMPLE_MILEAGE) : params.get("mileage") || "";
  const user = useCurrentUser();

  const [state, setState] = useState(() =>
    sample ? { status: "ready", report: normaliseReport(SAMPLE_REPORT) } : { status: "loading" }
  );
  const [attempt, setAttempt] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  const [showAccount, setShowAccount] = useState(false);
  const requestRef = useRef(null);

  useEffect(() => {
    if (sample) return;
    if (!isValidReg(registration)) {
      setState({
        status: "error",
        error: { title: "No registration to check", message: "Enter a registration on the home page to get a report.", missing: true },
      });
      return;
    }

    // React runs effects twice in development; this stops a second (paid) API call.
    const requestKey = `${registration}|${attempt}`;
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;
    setState({ status: "loading" });

    axios
      .get(`${API_URL}/api/faults`, {
        params: { registration, mileage: mileageParam || undefined },
        timeout: 60000,
      })
      .then(({ data }) => {
        if (requestRef.current === requestKey) setState({ status: "ready", report: normaliseReport(data) });
      })
      .catch((error) => {
        console.error("Error fetching report:", error);
        if (requestRef.current === requestKey) setState({ status: "error", error: describeError(error, registration) });
      });
  }, [sample, registration, mileageParam, attempt]);

  const report = state.status === "ready" ? state.report : null;
  const vehicleName = report ? [report.vehicleInfo.make, report.vehicleInfo.model].filter(Boolean).join(" ") : "";
  usePageTitle(registration ? `${formatReg(registration)}${vehicleName ? ` ${vehicleName}` : ""} report` : "Vehicle report");

  const retry = () => setAttempt((count) => count + 1);

  if (state.status === "loading") {
    return (
      <PageShell>
        <div
          className={`${container} flex min-h-[60vh] flex-col items-center justify-center py-16 text-center`}
          role="status"
          aria-live="polite"
        >
          <Plate registration={registration} size="lg" />
          <span className="mt-8 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" aria-hidden="true" />
          <h1 className="mt-6 text-xl font-semibold text-slate-900">Building your report</h1>
          <p className="mt-2 max-w-md text-slate-600">
            We're getting the MOT history from the DVSA and analysing the known faults for this car. This can take up to a
            minute.
          </p>
        </div>
      </PageShell>
    );
  }

  if (state.status === "error") {
    const { error } = state;
    return (
      <PageShell>
        <div className={`${container} flex min-h-[60vh] items-center justify-center py-16`}>
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" aria-hidden="true" />
            {registration && !error.missing && (
              <div className="mt-4">
                <Plate registration={registration} />
              </div>
            )}
            <h1 className="mt-4 text-xl font-semibold text-slate-900">{error.title}</h1>
            <p className="mt-2 text-slate-600">{error.message}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {!error.notFound && !error.missing && (
                <button type="button" onClick={retry} className={buttons.primary}>
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Try again
                </button>
              )}
              <Link to="/#search" className={error.notFound || error.missing ? buttons.primary : buttons.secondary}>
                New check
              </Link>
              {error.notFound && (
                <Link to="/manual-search" className={buttons.secondary}>
                  Search by make and model
                </Link>
              )}
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  const { vehicleInfo, motHistory, faults, summary, kpis, riskScore, analysisFailed } = report;
  const mot = summariseMot(motHistory);
  const mileage = analyseMileage(motHistory, mileageParam);
  const tiles = buildSummaryTiles({ mot, mileage, vehicleInfo });
  const findings = mileageFindings(mileage);
  const sortedFaults = sortFaults(faults);
  const keyRisks = stringList(summary.keyRisks);
  const recommendations = stringList(summary.recommendations);
  const showAnalysis = !analysisFailed;
  const showRisk = showAnalysis && riskScore !== null;
  const hasSummary = Boolean(summary.overallAssessment) || keyRisks.length > 0 || recommendations.length > 0;
  const vehicleData = { registration, make: vehicleInfo.make, model: vehicleInfo.model, faults };

  const details = [
    ["First registered", formatDate(vehicleInfo.firstUsedDate || vehicleInfo.registrationDate)],
    ["Fuel", vehicleInfo.fuelType],
    ["Engine size", vehicleInfo.engineSize && `${vehicleInfo.engineSize} cc`],
    ["Colour", vehicleInfo.primaryColour],
    ["Mileage entered", mileage.entered !== null && `${formatNumber(mileage.entered)} miles`],
    ["MOT tests", String(mot.total)],
  ].filter(([, value]) => value);

  const handleSave = async () => {
    setSaveState("saving");
    try {
      await saveSearch(vehicleData);
      setSaveState("saved");
    } catch (error) {
      console.error("Error saving search:", error);
      setSaveState("error");
    }
  };

  return (
    <PageShell>
      {/* Vehicle header */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} py-8 sm:py-10`}>
          <p className="text-sm text-slate-500">
            {sample ? "Sample report · example data" : `Vehicle report · ${formatDate(new Date())}`}
          </p>
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
              <Plate registration={registration} size="lg" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {vehicleName || formatReg(registration)}
                </h1>
                <p className="mt-1 text-slate-600">{vehicleSummaryLine(vehicleInfo)}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 print:hidden">
              <button type="button" onClick={() => window.print()} className={buttons.secondary}>
                <Printer className="h-4 w-4" aria-hidden="true" />
                Print or save as PDF
              </button>
              <Link to="/#search" className={buttons.primary}>
                <Search className="h-4 w-4" aria-hidden="true" />
                {sample ? "Check your car" : "New check"}
              </Link>
            </div>
          </div>
          <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3 lg:grid-cols-6">
            {details.map(([label, value]) => (
              <div key={label}>
                <dt className="text-slate-500">{label}</dt>
                <dd className="mt-0.5 font-medium text-slate-900">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <div className={`${container} space-y-14 py-10 sm:py-12`}>
        {sample && (
          <p className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 print:hidden">
            <span className="font-semibold">This is a sample report.</span> It uses example data to show exactly what
            you get when you check a real car.
          </p>
        )}

        {analysisFailed && (
          <div className="flex flex-col gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">The fault analysis didn't finish this time</p>
                <p className="mt-1">The MOT history below is complete. Try again to get the known faults and risk score.</p>
              </div>
            </div>
            <button type="button" onClick={retry} className={`${buttons.secondary} shrink-0 print:hidden`}>
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Try again
            </button>
          </div>
        )}

        {/* Headline facts */}
        <section aria-labelledby="summary-heading">
          <h2 id="summary-heading" className="sr-only">
            Summary
          </h2>
          <div className="grid gap-6 lg:grid-cols-3">
            {showRisk && (
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <h3 className="text-sm text-slate-500">Risk score</h3>
                <div className="mt-2">
                  <RiskScore score={riskScore} />
                </div>
                <p className="mt-4 text-sm leading-6 text-slate-600">
                  Combines the MOT record with {sortedFaults.length} known {sortedFaults.length === 1 ? "fault" : "faults"}{" "}
                  for this model.
                </p>
              </div>
            )}
            <div
              className={`grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 ${
                showRisk ? "lg:col-span-2" : "lg:col-span-3 lg:grid-cols-4"
              }`}
            >
              <StatTile {...tiles.motStatus} />
              <StatTile {...tiles.lastTest} />
              <StatTile {...tiles.mileage} />
              <StatTile {...tiles.recall} />
            </div>
          </div>
          {showAnalysis && kpis && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm text-slate-500">Model ratings (estimates)</h3>
              <ModelRatings kpis={kpis} />
            </div>
          )}
        </section>

        {showAnalysis && hasSummary && (
          <section aria-labelledby="assessment-heading" className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 id="assessment-heading" className="text-xl font-bold tracking-tight text-slate-900">
                Summary
              </h2>
              {summary.overallAssessment && <p className="mt-3 leading-7 text-slate-700">{summary.overallAssessment}</p>}
              {keyRisks.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-900">Main risks</h3>
                  <ul className="mt-3 space-y-2">
                    {keyRisks.map((risk) => (
                      <li key={risk} className="flex gap-2.5 text-sm text-slate-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {recommendations.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 lg:col-span-5">
                <h3 className="font-semibold text-slate-900">Before you buy</h3>
                <ul className="mt-4 space-y-3">
                  {recommendations.map((recommendation) => (
                    <li key={recommendation} className="flex gap-3 text-sm text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                      {recommendation}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {showAnalysis && sortedFaults.length > 0 && (
          <section aria-labelledby="faults-heading">
            <SectionTitle id="faults-heading" title="Faults to check" count={sortedFaults.length}>
              Ranked by risk. Repair costs are estimates at the higher end of UK prices.
            </SectionTitle>
            <div className="pt-6">
              <FaultList faults={sortedFaults} />
            </div>
          </section>
        )}

        <section aria-labelledby="mot-heading">
          <SectionTitle id="mot-heading" title="MOT history" count={mot.total}>
            {mot.total > 0 &&
              `${mot.passes} passed · ${mot.fails} failed · ${mot.advisoryCount} ${mot.advisoryCount === 1 ? "advisory" : "advisories"} in total`}
          </SectionTitle>

          {mot.total === 0 ? (
            <p className="pt-6 text-slate-600">
              There are no MOT tests on record for this vehicle. Most cars have their first MOT at three years old.
            </p>
          ) : (
            <div className="grid gap-8 pt-6 lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-7">
                <MotTestList tests={mot.tests} />
              </div>
              <aside className="order-first lg:order-last lg:col-span-5">
                <div className="rounded-lg border border-slate-200 bg-white p-5 lg:sticky lg:top-24">
                  <h3 className="font-semibold text-slate-900">Mileage at each MOT</h3>
                  <p className="mt-1 text-sm text-slate-500">Odometer readings recorded by the tester, in miles</p>
                  {mileage.readings.length >= 2 && (
                    <div className="mt-4">
                      <MileageChart readings={mileage.readings} />
                    </div>
                  )}
                  {findings.length > 0 && (
                    <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
                      {findings.map((finding) => (
                        <Finding key={finding.title} {...finding} />
                      ))}
                    </div>
                  )}
                  {mileage.readings.length > 0 && (
                    <div className="mt-5 border-t border-slate-100 pt-4">
                      <MileageTable readings={mileage.readings} />
                    </div>
                  )}
                </div>
              </aside>
            </div>
          )}
        </section>

        {!sample && (
          <section className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center sm:justify-between print:hidden">
            <div>
              <h2 className="font-semibold text-slate-900">{saveState === "saved" ? "Saved to My searches" : "Keep this report"}</h2>
              <p className="mt-1 text-sm text-slate-600">
                {saveState === "error"
                  ? "We couldn't save it just now. Please try again."
                  : user
                  ? "Save it to your account so you can find it again later."
                  : "Create a free account to save reports and find them again later."}
              </p>
            </div>
            {user ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={saveState === "saving" || saveState === "saved"}
                className={`${saveState === "saved" ? buttons.secondary : buttons.primary} shrink-0`}
              >
                {saveState === "saved" ? (
                  <BookmarkCheck className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Bookmark className="h-4 w-4" aria-hidden="true" />
                )}
                {saveState === "saved" ? "Saved" : saveState === "saving" ? "Saving…" : "Save to My searches"}
              </button>
            ) : (
              <button type="button" onClick={() => setShowAccount(true)} className={`${buttons.primary} shrink-0`}>
                <Bookmark className="h-4 w-4" aria-hidden="true" />
                Create account to save
              </button>
            )}
          </section>
        )}

        <p className="border-t border-slate-200 pt-6 text-xs leading-5 text-slate-500">
          MOT results, mileage readings and vehicle details come from the DVSA. The risk score, ratings, faults and repair
          costs are generated by AI from that record and the known issues for this model, so treat them as guidance rather
          than a guarantee of the vehicle's condition. If you're unsure, have the car inspected by a qualified mechanic.
        </p>
      </div>

      {showAccount && (
        <AccountCreation
          initialMode="signup"
          vehicleData={vehicleData}
          onClose={() => setShowAccount(false)}
          onSuccess={(_, { saved } = {}) => {
            setShowAccount(false);
            if (saved) setSaveState("saved");
          }}
        />
      )}
    </PageShell>
  );
}
